import { readStorage, writeStorage } from "../safe-storage";
import {
  ATTRIBUTION_KEY,
  HEADLINE_COOKIE_NAME,
  SESSION_COOKIE_NAME,
  SESSION_KEY,
  SESSION_TTL_MS,
  UUID_V4_RE,
  VISITOR_COOKIE_NAME,
} from "./config";

export interface Attribution {
  yclid?: string;
  gclid?: string;
  fbclid?: string;
  etext?: string;
  ybaip?: string;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
}

interface SessionData {
  id: string;
  ts: number;
}

// The one cookie reader. Anchored on `^` or `; ` so a cookie whose name merely
// ENDS with the one asked for (`x_lc_vid`) can never be mistaken for it.
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match?.[1];
}

/**
 * The server-owned first-party visitor cookie, or undefined when it is absent
 * (a first-ever visit, before the SSR response sets it). Callers that need a
 * value for a batch envelope use `getVisitorId()`; callers that need to know
 * whether this browser is already known — the form and web-chat submits, which
 * pass it to the server so it can join the submit to the anonymous session —
 * use this.
 */
export function readVisitorIdCookie(): string | undefined {
  return readCookie(VISITOR_COOKIE_NAME);
}

export function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getVisitorId(): string {
  if (typeof document === "undefined") return "";
  // No cookie yet: use a throwaway id for this batch rather than dropping the
  // events. It is deliberately NOT persisted — the server owns the cookie.
  return readVisitorIdCookie() ?? generateId();
}

export function isSessionExpired(ts: number): boolean {
  return Date.now() - ts >= SESSION_TTL_MS;
}

export function getSession(): SessionData {
  // Prefer the server-owned `_lc_sid` cookie so JS-side events share the same
  // session_id as the SSR-written page_view (see server/lib/ssr-pageview.ts).
  // A present cookie is always non-expired (the browser drops it past maxAge).
  const cookieSid = readCookie(SESSION_COOKIE_NAME);
  if (cookieSid && UUID_V4_RE.test(cookieSid)) {
    const session: SessionData = { id: cookieSid, ts: Date.now() };
    writeStorage("local", SESSION_KEY, JSON.stringify(session));
    return session;
  }
  try {
    const raw = readStorage("local", SESSION_KEY);
    if (raw) {
      const data = JSON.parse(raw) as SessionData;
      if (!isSessionExpired(data.ts)) {
        data.ts = Date.now();
        writeStorage("local", SESSION_KEY, JSON.stringify(data));
        return data;
      }
    }
  } catch {}
  const session: SessionData = { id: generateId(), ts: Date.now() };
  writeStorage("local", SESSION_KEY, JSON.stringify(session));
  return session;
}

export function readClickId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const raw = new URLSearchParams(window.location.search).get("lc_cid");
  return raw && UUID_V4_RE.test(raw) ? raw : undefined;
}

export function parseAttributionFromUrl(url: string): Attribution {
  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    return {};
  }

  const result: Attribution = {};

  const clickIds = ["yclid", "gclid", "fbclid", "etext", "ybaip"] as const;
  for (const key of clickIds) {
    const val = params.get(key);
    if (val) {
      result[key] = val;
    }
  }

  const utmKeys = ["source", "medium", "campaign", "content", "term"] as const;
  const utm: NonNullable<Attribution["utm"]> = {};
  let hasUtm = false;
  for (const key of utmKeys) {
    const val = params.get(`utm_${key}`);
    if (val) {
      utm[key] = val;
      hasUtm = true;
    }
  }
  if (hasUtm) {
    result.utm = utm;
  }

  return result;
}

export function getAttribution(): Attribution {
  const fromUrl = parseAttributionFromUrl(window.location.href);
  if (Object.keys(fromUrl).length > 0) {
    writeStorage("local", ATTRIBUTION_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }

  try {
    const stored = readStorage("local", ATTRIBUTION_KEY);
    if (stored) return JSON.parse(stored) as Attribution;
  } catch {}

  return {};
}

export interface CheckoutAttribution {
  yclid?: string;
  gclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export function getCheckoutAttribution(): CheckoutAttribution {
  if (typeof window === "undefined") return {};
  const a = getAttribution();
  const out: CheckoutAttribution = {};
  if (a.yclid) out.yclid = a.yclid;
  if (a.gclid) out.gclid = a.gclid;
  if (a.utm?.source) out.utm_source = a.utm.source;
  if (a.utm?.medium) out.utm_medium = a.utm.medium;
  if (a.utm?.campaign) out.utm_campaign = a.utm.campaign;
  if (a.utm?.content) out.utm_content = a.utm.content;
  if (a.utm?.term) out.utm_term = a.utm.term;
  return out;
}

// Dynamic-headline reward attribution (ADR-0069). The SSR resolver sets a
// server-owned `_lc_hv` cookie (`clusterId.variantId.epoch`, httpOnly:false) when
// a headline variant is assigned. Tagging queued events with `{cluster, variant}`
// is the bandit's reward signal: the hourly alloc job (gc-sync
// POST /sync/headline-alloc) counts `form_submit` / `registration_submit` /
// `page_engaged` per `event_data->>'variant'`. The cookie exists only on
// headline-active pages, so events elsewhere are untouched.
// See server/lib/headline-select.ts + docs/specs/website/dynamic-headline-bandit.md.
export function parseHeadlineCookie(
  raw: string | undefined | null,
): { cluster: string; variant: number } | null {
  if (!raw) return null;
  const parts = raw.split(".");
  if (parts.length !== 3) return null;
  const [cluster, variantStr] = parts;
  if (!cluster || !/^\d+$/.test(variantStr)) return null;
  const variant = Number(variantStr);
  if (!Number.isSafeInteger(variant)) return null;
  return { cluster, variant };
}

export function getHeadlineAssignment(): { cluster: string; variant: number } | null {
  const raw = readCookie(HEADLINE_COOKIE_NAME);
  return raw ? parseHeadlineCookie(decodeURIComponent(raw)) : null;
}

export function tagEventDataWithHeadline(
  data: Record<string, unknown>,
  assignment: { cluster: string; variant: number } | null,
): Record<string, unknown> {
  if (!assignment || data.variant !== undefined) return data;
  return { ...data, cluster: assignment.cluster, variant: assignment.variant };
}
